<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="annotation-details-playground" ref="playground">
    <vue-draggable-resizable
      v-if="selectedFeature && selectedFeature.properties && reload"
      v-show="displayAnnotDetails"
      class="draggable"
      :parent="true"
      :resizable="false"
      drag-handle=".drag"
      @dragstop="dragStop"
      :w="width" h='auto' :x="positionAnnotDetails.x" :y="positionAnnotDetails.y"
      ref="detailsPanel"
    >
      <div class="accent-bar"></div>

      <div class="actions">
        <div class="title-wrap">
          <h1>{{ $t('current-selection') }}</h1>
          <!-- geometry-type chip: adapts to the selected annotation's drawing type
               (point / line / rectangle / polygon) read from its WKT location -->
          <span v-if="geometryLabel" class="geom-chip">
            <span class="geom-dot"></span>{{ geometryLabel }}
          </span>
        </div>
        <button class="drag button is-small close">
          <i class="fas fa-arrows-alt"></i>
        </button>
        <button class="button is-small close" @click="displayAnnotDetails = false">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="annotation-details-container">
        <annotation-details
          v-if="selectedFeature.properties.annot.hasOwnProperty('user')"
          :annotation="selectedFeature.properties.annot"
          :terms="terms"
          :images="images"
          :slices="slices"
          :profiles="profiles"
          :tracks="tracks"
          :users="allUsers"
          :showImageInfo="false"
          :showChannelInfo="showChannelInfo"
          :key="selectedFeature.id"
          :showComments="showComments"
          @addTerm="$emit('addTerm', $event)"
          @addTrack="$emit('addTrack', $event)"
          @updateTerms="$emit('updateTermsOrTracks', annot)"
          @updateTracks="$emit('updateTermsOrTracks', annot)"
          @updateProperties="$emit('updateProperties')"
          @select="$emit('select', $event)"
          @centerView="$emit('centerView', ($event) ? $event : annot)"
          @deletion="$emit('delete', annot)"
          @searchSimilarAnnotations="searchSimilarAnnotations"
        />

        <div v-else class="simple-details-pad">
          <annotation-simple-details
            :annotation="selectedFeature.properties.annot"
            @centerView="$emit('centerView', ($event) ? $event : annot)"
          />
        </div>
      </div>

      <!-- HACK for prev/next linked annotation shortkeys -->
      <annotation-links-preview
        v-show="false"
        :index="index"
        :show-main-annotation="false"
        :show-select-all-button="false"
        :allow-annotation-selection="true"
        :annotation="selectedFeature.properties.annot"
        :images="images"
        @select="$emit('select', $event)"
      />
    </vue-draggable-resizable>
  </div>
</template>

<script>
import VueDraggableResizable from 'vue-draggable-resizable';
import {Ims, UserCollection} from '@/api';
import {geometryTypeKey} from '@/utils/geometry-utils';
import AnnotationDetails from '@/components/annotations/AnnotationDetails';
import AnnotationLinksPreview from '@/components/annotations/AnnotationLinksPreview';
import AnnotationSimpleDetails from '@/components/viewer/annotations/AnnotationSimpleDetails';

export default {
  name: 'annotations-details-container',
  components: {
    AnnotationDetails,
    AnnotationLinksPreview,
    AnnotationSimpleDetails,
    VueDraggableResizable,
  },
  props: {
    index: String,
  },
  data() {
    return {
      width: 320,
      projectUsers: [],
      reload: true,
      showComments: false
    };
  },
  computed: {
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    images() {
      if (this.imageWrapper.imageGroup) {
        return [this.image, ...this.imageWrapper.imageGroup.imageInstances];
      }
      return [this.image];
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    showChannelInfo() {
      return this.imageWrapper.activeSlices && this.imageWrapper.activeSlices.length > 1;
    },
    profiles() {
      return this.imageWrapper.profile ? [this.imageWrapper.profile] : [];
    },
    displayAnnotDetails: {
      get() {
        return this.imageWrapper.selectedFeatures.displayAnnotDetails;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setDisplayAnnotDetails', value);
      }
    },
    positionAnnotDetails: {
      get() {
        return this.imageWrapper.selectedFeatures.positionAnnotDetails;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setPositionAnnotDetails', value);
      }
    },
    selectedFeature() {
      return this.$store.getters[this.imageModule + 'selectedFeature'];
    },
    allUsers() {
      return this.projectUsers;
    },
    annot() {
      return this.selectedFeature ? this.selectedFeature.properties.annot : {};
    },
    geometryKey() {
      return geometryTypeKey(this.annot.location);
    },
    geometryLabel() {
      return this.geometryKey ? this.$t(this.geometryKey) : '';
    },
    terms() {
      return this.$store.getters['currentProject/terms'] || [];
    },
    tracks() {
      return this.imageWrapper.tracks.tracks;
    }
  },
  watch: {
    selectedFeature() {
      if (this.selectedFeature) {
        // Re-open the selection panel whenever an annotation is (re)selected — otherwise
        // closing it via the X (displayAnnotDetails=false) leaves it stuck closed until a
        // page refresh resets the Vuex state.
        this.displayAnnotDetails = true;
        let targetAnnot = this.imageWrapper.selectedFeatures.showComments;
        this.showComments = (targetAnnot === this.annot.id);
        if (targetAnnot !== null) {
          this.$store.commit(this.imageModule + 'setShowComments', null);
        }
      }
    }
  },
  methods: {
    async fetchUsers() {
      let collection = new UserCollection({
        filterKey: 'project',
        filterValue: this.image.project.id,
      });

      this.projectUsers = (await collection.fetchAll()).array;
    },

    dragStop(x, y) {
      this.positionAnnotDetails = {x, y};
    },

    async handleResize() {
      await this.$nextTick(); // wait for update of clientWidth and clientHeight to their new values

      if (this.$refs.playground) {
        let maxX = Math.max(this.$refs.playground.clientWidth - this.width, 0);
        let height = 500;
        if (this.$refs.detailsPanel) {
          height = this.$refs.detailsPanel.height;
        }
        let maxY = Math.max(this.$refs.playground.clientHeight - height, 0);
        let x = Math.min(this.positionAnnotDetails.x, maxX);
        let y = Math.min(this.positionAnnotDetails.y, maxY);
        this.positionAnnotDetails = {x, y};

        // HACK to force the component to recreate and take into account new (x,y) ; should no longer be
        // necessary with version 2 of vue-draggable-resizable
        this.reload = false;
        this.$nextTick(() => this.reload = true);
      }
    },
    async searchSimilarAnnotations() {
      let data = (await Ims.instance.api.get(
        'retrieval/search',
        {
          params: {
            annotation: this.selectedFeature.properties.annot.id,
            nrt_neigh: 10 // eslint-disable-line camelcase
          }
        }
      )).data;
      this.$store.commit(this.imageModule + 'setShowSimilarAnnotations', true);
      this.$store.commit(this.imageModule + 'setSimilarAnnotations', data);
      this.$store.commit(this.imageModule + 'setQueryAnnotation', this.selectedFeature.properties.annot);
    }
  },
  created() {
    this.fetchUsers();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.$eventBus.$on('updateMapSize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
    this.$eventBus.$off('updateMapSize', this.handleResize);
  }
};
</script>

<style scoped>
.annotation-details-playground {
  position: absolute;
  /* UI-7 B5: right-anchored, narrow drag band so the Current Selection panel ALWAYS
     opens on the right (default x=0 sits at this band's left edge = right of screen)
     and can never reach the top-left CanvasToolbar flyout. Drag/clamp logic unchanged. */
  left: auto;
  top: 3.5rem;
  right: 4.5rem;
  bottom: 2em;
  width: 21rem;               /* panel is 320px (~20rem) + ~1rem of vertical drag room */
  max-width: calc(100% - 8rem);
  pointer-events: none; /* to allow selection of elements below it */
  /* Above every map overlay regardless of DOM order - the dock-layout restructure once put
     this container before .map-body and the whole card painted BEHIND the slide. */
  z-index: 50;
  /* background: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(0, 0, 0, 0.5); */
}

/* UI-5: ZayaAI selection panel — white flyout card matching the viewer panels */
.draggable {
  background: #fff;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e8ee;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 17, 22, 0.14), 0 2px 6px rgba(15, 17, 22, 0.06);
  pointer-events: auto;
  overflow: hidden;
}

/* 3px accent strip at the very top of the card (design language) */
.accent-bar {
  height: 3px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #1e63d0, #1750a8);
}

.actions {
  padding: 9px 10px;
  background: #fff;
  border-bottom: 1px solid #eef0f4;
  display: flex;
  align-items: center;
  gap: 6px;
}

.title-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

h1 {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #8a90a0;
  padding: 0;
  flex: 0 0 auto;
  text-align: left;
}

/* geometry-type chip next to the title */
.geom-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px 2px 7px;
  background: #f1f3f7;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 500;
  color: #4a5160;
}
.geom-dot {
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: #1e63d0;
  flex: 0 0 auto;
}

.actions .button {
  margin-left: 0;
  width: 26px;
  height: 26px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  color: #8a91a1;
}

.actions .button.drag {
  cursor: move;
}

.actions .button:hover {
  background: #f2f4f8;
  color: #1e63d0;
}

/* the close (X) is the last action button */
.actions .button:last-child:hover {
  background: #fbeae9;
  color: #c8443c;
}

.annotation-details-container {
  padding: 0;
  overflow: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* the algo/simple fallback keeps the old comfortable padding */
.simple-details-pad {
  padding: 0.85em 1em;
}
</style>

<style>
.dragging .button.drag {
  background-color: #1e63d0;
  color: #fff;
}

.annotation-details-playground .draggable {
  z-index: 15 !important;
}
</style>
